"use client";

import * as React from "react";
import { PaletteIcon } from "lucide-react";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "@/components/ui/popover";

/*
  Extend's document color picker, cut down to what the editor's annotation
  tools need: a swatch trigger and a popover with the browser's color input
  and a hex field. The registry version is built on Base UI's popover.
*/

const HEX_PATTERN = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i;

function normalizeHex(value: string): string | null {
  const match = HEX_PATTERN.exec(value.trim());
  if (!match) return null;
  const digits =
    match[1].length === 3
      ? match[1]
          .split("")
          .map((digit) => digit + digit)
          .join("")
      : match[1];
  return `#${digits.toLowerCase()}`;
}

type InlineRegistryIconProps = Omit<
  React.ComponentProps<"svg">,
  "children" | "strokeWidth"
> & { strokeWidth?: number };

export type ColorPickerProps = {
  value?: string;
  color?: string;
  onChange?: (value: string) => void;
  label?: string;
  disabled?: boolean;
  className?: string;
  icon?: React.ComponentType<InlineRegistryIconProps>;
  rainbowTrigger?: boolean;
};

export function ColorPicker({
  value: valueProp,
  color,
  onChange,
  label = "Choose Color",
  disabled = false,
  className,
  icon: IconComponent = PaletteIcon,
  rainbowTrigger = false
}: ColorPickerProps) {
  const value = valueProp ?? color ?? "";
  const hex = normalizeHex(value) ?? "";
  const [draft, setDraft] = React.useState(hex);
  React.useEffect(() => setDraft(hex), [hex]);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size={rainbowTrigger ? "icon-xs" : "sm"}
          disabled={disabled}
          aria-label={label}
          title={label}
          className={cn("gap-1.5", className)}
        >
          {rainbowTrigger ? null : <IconComponent className="size-4" />}
          <span
            aria-hidden="true"
            className="size-3.5 rounded-full ring-1 ring-line-3"
            style={{ backgroundColor: value || undefined }}
          />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-56">
        <span className="text-sm font-medium text-fg-2">{label}</span>
        <input
          type="color"
          aria-label={label}
          value={hex || undefined}
          onChange={(event) => onChange?.(event.target.value)}
          className="h-24 w-full cursor-pointer rounded-md bg-transparent"
        />
        <Input
          aria-label="Hex Color"
          value={draft}
          onChange={(event) => {
            setDraft(event.target.value);
            const next = normalizeHex(event.target.value);
            if (next) onChange?.(next);
          }}
          className="font-mono"
        />
      </PopoverContent>
    </Popover>
  );
}
